import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { withClarityTheme } from '@verifime/styles';
import { useState } from 'react';

type XpmContactSupportModalProps = {
  open: boolean;
  organisationId: string;
  onClose: () => void;
  onSubmit: (message: string) => void;
};

/**
 * Hardcoded values from Figma that don't have corresponding design tokens:
 * - Modal max width: 500px (between sizeXs=350px and sizeSm=600px)
 * - Container border radius: 4px (no matching borderRadius token)
 * - Message field height: 140px (Figma: h-[140px])
 * - Button height: 44px (Figma: h-[44px])
 */

const XpmContactSupportModalComponent = ({
  open,
  organisationId,
  onClose,
  onSubmit,
}: Readonly<XpmContactSupportModalProps>) => {
  const [message, setMessage] = useState('');

  const handleSubmit = () => {
    onSubmit(message);
    setMessage('');
  };

  const handleClose = () => {
    setMessage('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          maxWidth: '500px', // Hardcoded: between sizeXs and sizeSm
          width: '100%',
          borderRadius: '4px', // Hardcoded: Figma rounded-[4px]
        },
      }}
    >
      <DialogContent
        sx={(theme) => ({
          px: theme.semantic.spacing.lg,
          py: theme.semantic.spacing.base,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.semantic.spacing.lg,
        })}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={(theme) => ({
            gap: theme.semantic.spacing['2xs'],
          })}
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            Contact Support
          </Typography>
          <IconButton onClick={handleClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Organisation ID - Read-only display */}
        <Stack
          sx={(theme) => ({
            gap: theme.semantic.spacing.xs,
          })}
        >
          <Typography variant="h6">
            Organisation ID
          </Typography>
          <Typography variant="body1">{organisationId}</Typography>
        </Stack>

        {/* Issue - Read-only display */}
        <Stack
          sx={(theme) => ({
            gap: theme.semantic.spacing.xs,
          })}
        >
          <Typography variant="h6">
            Issue
          </Typography>
          <Typography variant="body1">XPM Integration Error</Typography>
        </Stack>

        {/* Message - Text input */}
        <Stack
          sx={(theme) => ({
            gap: theme.semantic.spacing.xs,
          })}
        >
          <Typography variant="h6">
            Message
          </Typography>
          <TextField
            multiline
            rows={4}
            placeholder="Placeholder"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                minHeight: '140px', // Hardcoded: Figma h-[140px]
              },
            }}
          />
        </Stack>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              height: '44px', // Hardcoded: Figma h-[44px]
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              height: '44px', // Hardcoded: Figma h-[44px]
            }}
          >
            Request Support
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export const XpmContactSupportModal = withClarityTheme(XpmContactSupportModalComponent);

export type { XpmContactSupportModalProps };
